// The `maintenance` family's Terminal renderers (epic #4391, #4435) — task and
// session LIFECYCLE. The tenancy half (account/installation/repository, plans,
// housekeeping crons) is `./maintenance-tenancy.ts`; split for size (biome's
// 800-line file cap), merged below into ONE export so `project.ts` sees one
// family, one table.
//
// ─── WHY THE PAYLOAD IS NEVER TRUSTED PRESENT ────────────────────────────────
//
// `TerminalRow.input`/`.output` are `unknown` and NULL whenever
// `visibleToMaintainer` is false (`wire/terminal.ts`'s `TerminalRow` doc). Several
// writers in this family set that flag false — `task.released`, the mothership's
// CI re-enqueue arm, every kind is possible on a row this build cannot see the
// payload of. `str`/`num` below type-guard and floor rather than assume, and every
// render here is written to produce an honest, if sparser, line on a withheld row
// rather than throw or fabricate a field.

import type {
  TerminalFailRenderContext,
  TerminalFailRendered,
  TerminalRenderContext,
  TerminalRendered,
} from '../../../wire/terminal';
import { attemptParams, terminalRendererGroup } from '../../../wire/terminal';
import { ScheduleRefusalKind } from '../../audit/payload';
import { ACCOUNT_VERBS_MAINTENANCE } from './account-verb';
import { num, str } from './maintenance-payload';
import { MAINTENANCE_TENANCY } from './maintenance-tenancy';

// ─── task.reenqueued (warn) ──────────────────────────────────────────────────
//
// A retry, not a death: `task.failed` is the terminal kind, this one means the
// ladder still has budget. Three writer shapes disagree — task-keyed
// (`{taskId, runnerId}`), CI-keyed (`{ciJobId, githubJobId, runnerId,
// source:'ci'}`, no taskId at all), and the box-report arm which sets
// `visibleToMaintainer: false` for this very kind (`nats-consumers.ts`) — so
// this reads `taskId` first, falls back to the CI job, and falls back again to a
// bare line naming nothing but the row when the payload is withheld.
function renderTaskReenqueued(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const taskId = str(row.input, 'taskId');
  if (taskId !== null) {
    const runId = str(row.input, 'runId');
    return {
      line: { key: 'terminal.task.reenqueued', params: {} },
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId, ...(runId !== null ? { runId } : {}) },
    };
  }
  const ciJobId = str(row.input, 'ciJobId');
  if (ciJobId !== null) {
    return {
      line: { key: 'terminal.task.reenqueued', params: { ciJob: ciJobId } },
      subject: { kind: 'task', id: ciJobId, label: `CI job ${ciJobId}` },
      refs: {},
    };
  }
  return {
    line: { key: 'terminal.task.reenqueued', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'task' },
    refs: {},
  };
}

// ─── fleet.event_dropped (warn) ──────────────────────────────────────────────
function renderFleetEventDropped(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const reason = str(row.input, 'reason') ?? 'unknown';
  const taskId = str(row.input, 'taskId');
  const runnerId = str(row.input, 'runnerId');
  return {
    line: { key: 'terminal.fleet.event_dropped', params: { reason } },
    subject:
      runnerId !== null
        ? { kind: 'box', id: runnerId, label: 'fleet event' }
        : { kind: 'session', id: row.sessionId, label: 'fleet event' },
    refs: { ...(taskId !== null ? { taskId } : {}) },
  };
}

// ─── worker.job_refused (warn) ───────────────────────────────────────────────
// Plan 101 F7: the loudest level a row WITHOUT an attempt can take — `fail` is
// the attempt-carrying arm, and this row names a lane job, not a task attempt.
function renderWorkerJobRefused(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const lane = str(row.input, 'lane') ?? 'unknown';
  return {
    line: { key: 'terminal.worker.job_refused', params: { lane } },
    // The subject vocabulary is closed and has no `lane`; the platform chain's
    // session is what this row hangs off, labelled with the lane it refused on.
    subject: { kind: 'session', id: row.sessionId, label: lane },
    refs: { sessionId: row.sessionId },
  };
}

// ─── platform_bug.screened (info) ────────────────────────────────────────────
// Plan 101 slice 05: the screen's answer for one staff bug report. The bug id is the
// label (the subject vocabulary has no `bug`); a withheld row reads `unknown`, never a
// made-up verdict. Never the report's text — the row does not carry it.
function renderPlatformBugScreened(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const bugId = str(row.input, 'bugId');
  const verdict = str(row.output, 'verdict') ?? 'unknown';
  const reason = str(row.output, 'reason') ?? 'none';
  return {
    line: { key: 'terminal.platform_bug.screened', params: { verdict, reason } },
    subject: { kind: 'session', id: row.sessionId, label: bugId ?? 'bug report' },
    refs: { sessionId: row.sessionId },
  };
}

// ─── task.failed (fail — attemptParams REQUIRED) ─────────────────────────────
function renderTaskFailed(ctx: TerminalFailRenderContext): TerminalFailRendered | null {
  const { row, attempt } = ctx;
  const taskId = str(row.input, 'taskId');
  if (taskId === null) return null;
  const reason = str(row.output, 'reason');
  // `runId` picked up the same way `task.reenqueued` above does, from the same
  // writer (`nats-consumers.ts`) — it is the only ref on this row that maps to a
  // route that exists (`/fleet/runs/:run_id`; `taskId` has none), and this is the
  // single most important line on a failure console to be able to link.
  const runId = str(row.input, 'runId');
  return {
    line: {
      key: 'terminal.task.failed',
      params: { ...attemptParams(attempt), ...(reason !== null ? { reason } : {}) },
    },
    subject: { kind: 'task', id: taskId, label: taskId },
    refs: { taskId, ...(runId !== null ? { runId } : {}) },
  };
}

// ─── task.released (info) ────────────────────────────────────────────────────
//
// The one writer (`nats-consumers.ts`) sets `visibleToMaintainer: false` — the
// payload is ALWAYS withheld on this kind, so this renders from row-level fields
// only — the session is the one real id the row carries. A restart the task
// had nothing to do with; not worth more than a tick.
function renderTaskReleased(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.task.released', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'task' },
    refs: {},
  };
}

// ─── task.paused (info) ──────────────────────────────────────────────────────
//
// #6748: a person's pause landed — the run stopped at a safe point and its task parked on
// the same machine. Rendered from row-level fields only, like `task.released`: the line is
// the event, and the task is reached through the session.
function renderTaskPaused(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.task.paused', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'task' },
    refs: {},
  };
}

// ─── task.cancelled (info) ────────────────────────────────────────────────────
//
// Two writer SHAPES, not just two field sets: `mcp-ports.ts` writes task-keyed
// (`{taskId, previousRunnerId, reason, tokenId}`), `retire-settled-pr.ts` /
// `review/wiring.ts` write PR-keyed (`{repo, prNumber, reason, via}` — no
// taskId at all, a batch of tasks in `output`). Branch on which shape is present
// rather than guess a merged one.
function renderTaskCancelled(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const taskId = str(row.input, 'taskId');
  const reason = str(row.input, 'reason') ?? 'cancelled';
  if (taskId !== null) {
    return {
      line: { key: 'terminal.task.cancelled', params: { reason } },
      subject: { kind: 'task', id: taskId, label: taskId },
      refs: { taskId },
    };
  }
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'prNumber');
  return {
    line: { key: 'terminal.task.cancelled', params: { reason } },
    subject:
      prNumber !== null
        ? { kind: 'pull_request', id: String(prNumber), label: repo ?? 'pull request' }
        : { kind: 'session', id: row.sessionId, label: repo ?? 'pull request' },
    refs: {
      ...(repo !== null ? { repoFullName: repo } : {}),
      ...(prNumber !== null ? { prNumber } : {}),
    },
  };
}

// ─── task.redispatched (decision — an operator re-drove a stuck task) ────────
function renderTaskRedispatched(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const taskId = str(row.input, 'taskId');
  if (taskId === null) return null;
  return {
    line: { key: 'terminal.task.redispatched', params: {} },
    subject: { kind: 'task', id: taskId, label: taskId },
    refs: { taskId },
  };
}

// ─── task.narrated (info) ─────────────────────────────────────────────────────
function renderTaskNarrated(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const taskId = str(row.input, 'taskId');
  if (taskId === null) return null;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const threadNumber = num(row.input, 'threadNumber');
  return {
    line: { key: 'terminal.task.narrated', params: {} },
    subject: { kind: 'task', id: taskId, label: taskId },
    refs: {
      taskId,
      ...(repo !== null ? { repoFullName: repo } : {}),
      ...(threadNumber !== null ? { issueNumber: threadNumber } : {}),
    },
  };
}

// ─── stalled_replay.redriven (info) ───────────────────────────────────────────
//
// Two shapes: a per-entity redrive (`{accountId, repo, entityKind, entityId}`)
// and the sweep summary (`{accountId}` only, counts in `output`). Both are
// worth a line; the summary's is generic.
function renderStalledReplayRedriven(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const entityId = str(row.input, 'entityId');
  if (entityId !== null) {
    return {
      line: { key: 'terminal.stalled_replay.redriven', params: {} },
      subject:
        repo !== null
          ? { kind: 'repo', id: repo, label: repo }
          : { kind: 'session', id: row.sessionId, label: 'stalled replay' },
      refs: { ...(repo !== null ? { repoFullName: repo } : {}) },
    };
  }
  const redriven = num(row.output, 'redriven') ?? 0;
  const accountId = str(row.input, 'accountId');
  return {
    line: { key: 'terminal.stalled_replay.redriven', params: { redriven } },
    subject:
      accountId !== null
        ? { kind: 'account', id: accountId, label: 'stalled-replay sweep' }
        : { kind: 'session', id: row.sessionId, label: 'stalled-replay sweep' },
    refs: {},
  };
}

// ─── schedule.fired (decision) / schedule.refused (warn) ─────────────────────
//
// #5363, the hourly `cron.schedules` lane. Both rows are tenant-anchored and
// `visibleToMaintainer`, and both name the ANCHOR repo (the one whose policy holds
// the schedule); a withheld payload still renders, on the session, naming nothing.
function scheduleSubject(
  ctx: TerminalRenderContext,
  fallback: string,
): TerminalRendered['subject'] {
  const anchor = str(ctx.row.input, 'anchor') ?? ctx.row.repoFullName;
  return anchor !== null
    ? { kind: 'repo', id: anchor, label: anchor }
    : { kind: 'session', id: ctx.row.sessionId, label: fallback };
}

function renderScheduleFired(ctx: TerminalRenderContext): TerminalRendered {
  const name = str(ctx.row.input, 'name') ?? 'schedule';
  const slot = str(ctx.row.input, 'slot') ?? '';
  const anchor = str(ctx.row.input, 'anchor') ?? ctx.row.repoFullName;
  return {
    line: { key: 'terminal.schedule.fired', params: { name, slot } },
    subject: scheduleSubject(ctx, name),
    refs: { ...(anchor !== null ? { repoFullName: anchor } : {}) },
  };
}

/** The refusal kinds the lane writes — each gets its own explaining line. */
const SCHEDULE_REFUSAL_KINDS: ReadonlySet<string> = new Set(Object.values(ScheduleRefusalKind));

function renderScheduleRefused(ctx: TerminalRenderContext): TerminalRendered {
  const name = str(ctx.row.input, 'name') ?? 'schedule';
  const reason = str(ctx.row.input, 'kind') ?? 'unknown';
  const anchor = str(ctx.row.input, 'anchor') ?? ctx.row.repoFullName;
  // A known kind renders its own sentence, naming the listed repo on the two repo
  // arms; a kind this build does not know (a newer writer) keeps the generic line
  // with the raw value, never a missing key.
  const line = SCHEDULE_REFUSAL_KINDS.has(reason)
    ? {
        key: `terminal.schedule.refusal.${reason}` as const,
        params: {
          name,
          repo: str(ctx.row.input, 'repo') ?? 'a listed repo',
          gap: str(ctx.row.input, 'gap') ?? 'unknown',
          max: num(ctx.row.input, 'max') ?? 0,
        },
      }
    : { key: 'terminal.schedule.refused' as const, params: { name, reason } };
  return {
    line,
    subject: scheduleSubject(ctx, name),
    refs: { ...(anchor !== null ? { repoFullName: anchor } : {}) },
  };
}

// ─── session.compacted / disabled / ineffective ──────────────────────────────
function renderSessionCompacted(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const dropped = num(row.output, 'droppedMessages') ?? 0;
  return {
    line: { key: 'terminal.session.compacted', params: { dropped } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

function renderSessionCompactionDisabled(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const reason = str(row.output, 'reason') ?? 'unknown';
  return {
    line: { key: 'terminal.session.compaction_disabled', params: { reason } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

function renderSessionCompactionIneffective(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.session.compaction_ineffective', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

// ─── session.cost_soft_warn (warn) ────────────────────────────────────────────
function renderSessionSoftWarn(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const threshold = num(row.output, 'threshold') ?? 0;
  const spendUsd = num(row.output, 'spendUsd') ?? 0;
  return {
    line: { key: 'terminal.session.cost_soft_warn', params: { threshold, spendUsd } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

// ─── session.completed (ok) ───────────────────────────────────────────────────
function renderSessionCompleted(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const costUsd = num(row.output, 'costUsd') ?? 0;
  return {
    line: { key: 'terminal.session.completed', params: { costUsd } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

// ─── session.escalated (decision — a policy threshold fired) ─────────────────
function renderSessionEscalated(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const category = str(row.output, 'category') ?? 'escalation';
  return {
    line: { key: 'terminal.session.escalated', params: { category } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

// ─── session.failed (warn — no attempt ladder on a session, so not 'fail') ───
function renderSessionFailed(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const cause = str(row.output, 'cause') ?? 'unknown';
  return {
    line: { key: 'terminal.session.failed', params: { cause } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

// ─── session.handed_back (info — a roll, not a verdict on the work, 02-3) ────
function renderSessionHandedBack(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.session.handed_back', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

/** The task + session lifecycle half of the family. `as const` locks every
 * entry's literal `level` (and the `'omit'` literal) BEFORE the spread below —
 * a spread of an un-const'd object widens `level` back to `string` and the
 * whole composed table stops discriminating (`wire/terminal.ts`'s
 * `terminalRendererGroup` doc). */
const MAINTENANCE_CORE = {
  'task.reenqueued': { level: 'warn', actor: 'system', render: renderTaskReenqueued },
  'fleet.event_dropped': { level: 'warn', actor: 'system', render: renderFleetEventDropped },
  // #4446: one row per box boot, census M13's instrument — routine box narration like the
  // `runner.update_*` hops, never an outcome a maintainer acts on.
  'runner.boot_hygiene_reported': 'omit',
  'worker.job_refused': { level: 'warn', actor: 'system', render: renderWorkerJobRefused },
  'platform_bug.screened': { level: 'info', actor: 'system', render: renderPlatformBugScreened },
  'task.failed': { level: 'fail', actor: 'coder', render: renderTaskFailed },
  'task.released': { level: 'info', actor: 'system', render: renderTaskReleased },
  'task.paused': { level: 'info', actor: 'system', render: renderTaskPaused },
  'task.cancelled': { level: 'info', actor: 'system', render: renderTaskCancelled },
  'task.redispatched': { level: 'decision', actor: 'human', render: renderTaskRedispatched },
  'task.narrated': { level: 'info', actor: 'system', render: renderTaskNarrated },
  // Housekeeping on a task already ended (its cancel/merge is the line the terminal shows);
  // the row is the audit trail of the delete, read on the audit page.
  'task.saved_branch_retired': 'omit',
  'stalled_replay.redriven': {
    level: 'info',
    actor: 'system',
    render: renderStalledReplayRedriven,
  },
  // #4435 step 4, and the AUTHORITATIVE entry (see project.ts's header, in full):
  // `task.run_event` ingests `run_heartbeat`/`tool_call`/`status` sub-events and
  // is doubly unreachable — the heartbeat discriminator is not even in the audit
  // payload, and `visibleToMaintainer: false` nulls what little the row carries.
  // The verbose run-event sub-kind table is scoped OUT of this slice; this is the
  // one place that says so.
  'task.run_event': 'omit',
  // A mechanical edge rewrite behind a regroup the terminal already shows (`plan.regrouped`).
  'task.dependencies_healed': 'omit',
  // The same follow-through at group level; the operator's `plan.regrouped` is the decision.
  'plan.superseded_regrouped': 'omit',
  // A mechanical DAG repair, visible to the maintainer on the audit chain with its before/after.
  'task.dependency_cycle_broken': 'omit',
  'schedule.fired': { level: 'decision', actor: 'system', render: renderScheduleFired },
  'schedule.refused': { level: 'warn', actor: 'system', render: renderScheduleRefused },
  // The watch's own bookkeeping: the terminal shows the raw task and its work, which are the story.
  'security_watch.filed': 'omit',
  'security_watch.raised': 'omit',
  'security_watch.deferred': 'omit',
  'security_watch.patched': 'omit',
  'session.compacted': { level: 'info', actor: 'coder', render: renderSessionCompacted },
  'session.compaction_disabled': {
    level: 'warn',
    actor: 'coder',
    render: renderSessionCompactionDisabled,
  },
  'session.compaction_ineffective': {
    level: 'warn',
    actor: 'coder',
    render: renderSessionCompactionIneffective,
  },
  'session.cost_soft_warn': { level: 'warn', actor: 'coder', render: renderSessionSoftWarn },
  'session.completed': { level: 'ok', actor: 'coder', render: renderSessionCompleted },
  'session.escalated': { level: 'decision', actor: 'system', render: renderSessionEscalated },
  'session.failed': { level: 'warn', actor: 'coder', render: renderSessionFailed },
  'session.handed_back': { level: 'info', actor: 'system', render: renderSessionHandedBack },
} as const;

/**
 * For the partition test only. `MAINTENANCE_CORE` and `MAINTENANCE_TENANCY` spread
 * into ONE `terminalRendererGroup` call below, so a key in both is not a
 * `ts(1117)` duplicate-key error — the later spread wins silently, and neither the
 * whole-vocabulary partition test nor the total-coverage test (one entry per
 * `AuditEventKind`, whatever the vocabulary's size) can see which half a composed
 * key actually came from.
 */
export function maintenanceCoreKeys(): readonly string[] {
  return Object.keys(MAINTENANCE_CORE);
}

/** The composed family. See the module header for why this is two files. */
export const TERMINAL_RENDERERS_MAINTENANCE = terminalRendererGroup({
  ...MAINTENANCE_CORE,
  ...MAINTENANCE_TENANCY,
  ...ACCOUNT_VERBS_MAINTENANCE,
});
